import { instance, parseAxiosError } from "./backendApi";
import md5 from 'blueimp-md5'

const localAuth = {
    get: () => JSON.parse(localStorage.getItem('biochemai.auth')),
    set: (auth) => localStorage.setItem('biochemai.auth', JSON.stringify(auth)),
    rm: () => localStorage.removeItem('biochemai.auth')
}

const login = async ({ username, password }, redirectTo) => {
    try {
        const auth = await instance.post('/v1/auth/login', {
            email: username,
            password: password
        }).then(response => response.data);
        localAuth.set(auth);
        return { redirectTo: redirectTo ?? '/redirect?to=/&update&notice=Login Successful&noticeType=success'};
    } catch (error) {
        throw parseAxiosError(error);
    }
}

const logout = async () => {
    const auth = localAuth.get();
    if (auth) {
        try {
            await instance.post('/v1/auth/logout', {
                refreshToken: auth.tokens.refresh.token
            });
            localAuth.rm()
            return '/redirect?to=/&update&notice=Logout Successful&noticeType=success';
        } catch (error) {
            console.error(parseAxiosError(error));
            localAuth.rm()
            return '/redirect?to=/&update&notice=Logout Maybe Successful&noticeType=success';
        }
    }
}

const checkError = async ({ status }) => {
    if (status === 401 || status === 403) {
        localAuth.rm();
        throw Error();
    }
    return null;
}

const checkAuth = async () => {
    const auth = localAuth.get();
    if (!auth) {
        throw new Error("Please login to continue");
    } else if (Date.parse(auth.tokens.access.expires) < Date.now()) {
        await refreshTokens();
        // throw new Error("Login expired, please login again");
    } else {
        return null;
    }
}

const getIdentity = async () => {
    const auth = localAuth.get();
    if (!auth) {
        throw new Error("Please login to continue");
    } else {
        return {
            fullName: auth.user.name, 
            avatar: `https://cravatar.cn/avatar/${computeGravatarHash(auth.email)}`,
            ...auth.user
        };
    }
}

const getPermissions = async () => {
    const auth = localAuth.get();
    if (!auth) {
        return null;
    } else {
        return auth.user.role;
    }
}

const getAccessToken = async () => {
    const auth = localAuth.get();
    if (!auth) {
        return null;
    } else {
        return auth.tokens.access.token;
    }
}

const refreshTokens = async () => {
    try {
        const auth = localAuth.get();
        if (!auth) {
            throw Error("Cannot find auth");
        }
        const { user, tokens } = await instance.post('/v1/auth/refresh-tokens', {
            refreshToken: auth.tokens.refresh.token
        }).then(response => response.data);
        if (user && tokens) {
            localAuth.set({ user, tokens });
        } else {
            throw Error("Refresh tokens with empty");
        }
        
    } catch (error) {
        throw parseAxiosError(error);
    }
}

const computeGravatarHash = (email) => {
  const _email = ((typeof email === 'string') ? email : '').trim().toLowerCase();
  const hash = md5(_email);
  return hash;
}

const signup = async ({ email, password, name }, redirectTo) => {
    try {
        const auth = await instance.post('/v1/auth/register', { email, password, name})
            .then(response => response.data);
        localAuth.set(auth);
        return { redirectTo: redirectTo ?? '/redirect?to=/&update&notice=Create Account Successful&noticeType=success'};
    } catch (error) {
        throw parseAxiosError(error);
    }
}

const isEmailTaken = async (email) => {
    try {
        const auth = await instance.post('/v1/auth/is-email-taken', { email })
            .then(response => response.data);
        const { isTaken } = auth;
        if (typeof isTaken !== "boolean") {
            throw Error("Unable to determine if the email is taken")
        }
        return isTaken;
    } catch (error) {
        throw parseAxiosError(error);
    }
}

const authProvider = {
    // send username and password to the auth server and get back credentials
    login,
    // when the dataProvider returns an error, check if this is an authentication error
    checkError,
    // when the user navigates, make sure that their credentials are still valid
    checkAuth,
    // remove local credentials and notify the auth server that the user logged out
    logout,
    // get the user's profile
    getIdentity,
    // get the user permissions (optional)
    getPermissions,
    // Custom
    getAccessToken,
    refreshTokens,
    signup,
    isEmailTaken
};

export default authProvider
